import { z } from 'zod';

import type { operations } from '@molchanovs/api-contract';
import {
  BUSINESS_ROLE_FREEDIVER,
  BUSINESS_ROLE_INSTRUCTOR,
  type BusinessRole,
} from '@entities/user';
import { crmDirectoryStartViewIdSchema } from '@entities/user-profile';

export const userProfilesDirectoryViewSchema = crmDirectoryStartViewIdSchema;

export type UserProfilesDirectoryView = z.infer<typeof userProfilesDirectoryViewSchema>;

export const USER_PROFILES_DIRECTORY_SORT_KEYS = [
  'displayName',
  'birthDate',
  'preferredLanguage',
  'instructorId',
  'shopifyId',
  'userOrigin',
  'country',
  'city',
  'freediverId',
  'registrationCount',
  'activityRegistrationsWeek',
  'activityRegistrationsMonth',
  'activityRegistrationsQuarter',
  'activityRegistrationsHalfYear',
  'activityRegistrationsYear',
  'activityRegistrationsAllTime',
  'highestDiscount',
  'volumeDiscount',
] as const;

export type UserProfilesDirectorySortKey = (typeof USER_PROFILES_DIRECTORY_SORT_KEYS)[number];

const USER_PROFILES_DIRECTORY_SORT_VALUES = [
  'displayName.asc',
  'displayName.desc',
  'birthDate.asc',
  'birthDate.desc',
  'preferredLanguage.asc',
  'preferredLanguage.desc',
  'instructorId.asc',
  'instructorId.desc',
  'shopifyId.asc',
  'shopifyId.desc',
  'userOrigin.asc',
  'userOrigin.desc',
  'country.asc',
  'country.desc',
  'city.asc',
  'city.desc',
  'freediverId.asc',
  'freediverId.desc',
  'registrationCount.asc',
  'registrationCount.desc',
  'activityRegistrationsWeek.asc',
  'activityRegistrationsWeek.desc',
  'activityRegistrationsMonth.asc',
  'activityRegistrationsMonth.desc',
  'activityRegistrationsQuarter.asc',
  'activityRegistrationsQuarter.desc',
  'activityRegistrationsHalfYear.asc',
  'activityRegistrationsHalfYear.desc',
  'activityRegistrationsYear.asc',
  'activityRegistrationsYear.desc',
  'activityRegistrationsAllTime.asc',
  'activityRegistrationsAllTime.desc',
  'highestDiscount.asc',
  'highestDiscount.desc',
  'volumeDiscount.asc',
  'volumeDiscount.desc',
] as const;

export const userProfilesDirectorySortSchema = z.enum(USER_PROFILES_DIRECTORY_SORT_VALUES);

export type UserProfilesDirectorySort = z.infer<typeof userProfilesDirectorySortSchema>;

export type UserProfilesDirectoryColumnId =
  | 'actions'
  | 'activityBtPrograms'
  | 'activityCertificates'
  | 'activityCourses'
  | 'activityRegistrationsAllTime'
  | 'activityRegistrationsHalfYear'
  | 'activityRegistrationsMonth'
  | 'activityRegistrationsQuarter'
  | 'activityRegistrationsWeek'
  | 'activityRegistrationsYear'
  | 'activitySchool'
  | 'activityWorkouts'
  | 'birthDate'
  | 'city'
  | 'country'
  | 'currentMembershipExpiration'
  | 'currentMembershipNumber'
  | 'currentMembershipStatus'
  | 'currentMembershipType'
  | 'freediverId'
  | 'highestDiscount'
  | 'inductingInstructor'
  | 'instructorId'
  | 'lastCertification'
  | 'lastDocumentStatus'
  | 'lastDocumentType'
  | 'lastDocumentValidUntil'
  | 'personalAddress'
  | 'preferredLanguage'
  | 'registrationsCount'
  | 'roles'
  | 'shopifyId'
  | 'shopifyTags'
  | 'submittingInstructor'
  | 'user'
  | 'userOrigin'
  | 'volumeDiscount';

export type UserProfilesDirectoryActivity =
  operations['getApiV1UserProfiles']['responses'][200]['content']['application/json']['data']['items'][number]['activity'];

export type UserProfilesDirectoryActivityRegistrationCounter =
  UserProfilesDirectoryActivity['registrations']['week'];
export type UserProfilesDirectoryActivityLearningStoredValueCounter =
  UserProfilesDirectoryActivity['workouts'];
export type UserProfilesDirectoryActivityLearningSourceListCounter =
  UserProfilesDirectoryActivity['courses'];
export type UserProfilesDirectoryActivitySchool = UserProfilesDirectoryActivity['school'];

export interface UserProfilesDirectoryColumnRecord {
  activity: UserProfilesDirectoryActivity;
  birthDate: string | null;
  city: string | null;
  currentMembership: {
    expiresAt: string | null;
    number: string | null;
    status: string | null;
    type: { id: string | null; name: string | null };
  } | null;
  country: string | null;
  discounts: { highest: number | null; volume: number | null };
  freediverId: string | null;
  inductingInstructor: { id: string; displayName: string | null } | null;
  instructorId: string | null;
  lastCertification: { certifiedAt: string; type: { id: string; label: string | null } } | null;
  lastDocument: {
    status: { id: string | null; label: string | null };
    type: { id: string | null; label: string | null };
    validUntil: string | null;
  } | null;
  personalAddress: string | null;
  preferredLanguage: string | null;
  registrationsCount: number | null;
  roles: readonly string[];
  shopify: { id: string | null; tags: readonly string[] };
  submittingInstructor: { id: string; displayName: string | null } | null;
  title: string;
  userOrigin: 'Shopify' | 'Move' | null;
}

type UserProfilesDirectoryColumnValue =
  | UserProfilesDirectoryActivityLearningStoredValueCounter
  | UserProfilesDirectoryActivityLearningSourceListCounter
  | UserProfilesDirectoryActivityRegistrationCounter
  | UserProfilesDirectoryActivitySchool
  | string
  | number
  | readonly string[]
  | null
  | undefined;
type UserProfilesDirectoryColumnCapability =
  | 'certification-types'
  | 'document-metadata'
  | 'membership'
  | 'none'
  | 'sensitive-fields';
type UserProfilesDirectoryColumnFormatter =
  | 'activity-counter'
  | 'activity-school'
  | 'document-status'
  | 'list'
  | 'membership-status'
  | 'roles'
  | 'text'
  | 'utc-date';
type UserProfilesDirectoryColumnDesktopPresentation = 'action' | 'identity' | 'roles' | 'value';
type UserProfilesDirectoryColumnMobileIntent = 'controls' | 'field' | 'header';
type UserProfilesDirectoryColumnSize =
  | 'actionCompact'
  | 'compact'
  | 'identity'
  | 'medium'
  | 'micro'
  | 'wide';

export interface UserProfilesDirectoryColumnVisibility {
  canReadCertificationTypes: boolean;
  canReadDocumentMetadata: boolean;
  canReadMembership: boolean;
  canReadSensitiveFields: boolean;
  view: UserProfilesDirectoryView | undefined;
}

export interface UserProfilesDirectoryColumnDefinition {
  capability: UserProfilesDirectoryColumnCapability;
  desktop: {
    className?: string;
    headerClassName?: string;
    presentation: UserProfilesDirectoryColumnDesktopPresentation;
    size:
      | UserProfilesDirectoryColumnSize
      | ((visibility: UserProfilesDirectoryColumnVisibility) => UserProfilesDirectoryColumnSize);
  };
  emptyValue: 'dash' | 'not-provided';
  formatter: UserProfilesDirectoryColumnFormatter;
  key: UserProfilesDirectoryColumnId;
  label: { fallback: string; key: string };
  mobile: {
    identity?: {
      value: (record: UserProfilesDirectoryColumnRecord) => string | null | undefined;
      views: readonly UserProfilesDirectoryView[];
    };
    intent: UserProfilesDirectoryColumnMobileIntent;
  };
  sortKey?: UserProfilesDirectorySortKey;
  value: (record: UserProfilesDirectoryColumnRecord) => UserProfilesDirectoryColumnValue;
}

type UserProfilesDirectoryViewColumnConfiguration = Record<
  UserProfilesDirectoryView | 'all',
  readonly UserProfilesDirectoryColumnId[]
>;

/**
 * The feature-local source of truth for each Directory field. Views choose only
 * these stable IDs; desktop table cells and mobile card fields share the same
 * label, value, presentation intent, capability, empty state, and sort metadata.
 */
export const USER_PROFILES_DIRECTORY_COLUMN_REGISTRY = {
  actions: {
    capability: 'none',
    desktop: {
      className: 'px-2 text-left align-middle',
      headerClassName: 'px-2 text-left',
      presentation: 'action',
      size: 'actionCompact',
    },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'actions',
    label: { fallback: 'Actions', key: 'crm.customerDirectory.columns.actions' },
    mobile: { intent: 'controls' },
    value: () => null,
  },
  activityBtPrograms: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityBtPrograms',
    label: { fallback: 'BT Programs', key: 'crm.customerDirectory.columns.activityBtPrograms' },
    mobile: { intent: 'field' },
    value: (record) => record.activity.btPrograms,
  },
  activityCertificates: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityCertificates',
    label: { fallback: 'Certificates', key: 'crm.customerDirectory.columns.activityCertificates' },
    mobile: { intent: 'field' },
    value: (record) => record.activity.certificates,
  },
  activityCourses: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityCourses',
    label: { fallback: 'Courses', key: 'crm.customerDirectory.columns.activityCourses' },
    mobile: { intent: 'field' },
    value: (record) => record.activity.courses,
  },
  activityRegistrationsAllTime: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityRegistrationsAllTime',
    label: {
      fallback: 'Regs All Time',
      key: 'crm.customerDirectory.columns.activityRegistrationsAllTime',
    },
    mobile: { intent: 'field' },
    sortKey: 'activityRegistrationsAllTime',
    value: (record) => record.activity.registrations.allTime,
  },
  activityRegistrationsHalfYear: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityRegistrationsHalfYear',
    label: {
      fallback: 'Regs Half Year',
      key: 'crm.customerDirectory.columns.activityRegistrationsHalfYear',
    },
    mobile: { intent: 'field' },
    sortKey: 'activityRegistrationsHalfYear',
    value: (record) => record.activity.registrations.halfYear,
  },
  activityRegistrationsMonth: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityRegistrationsMonth',
    label: {
      fallback: 'Regs Month',
      key: 'crm.customerDirectory.columns.activityRegistrationsMonth',
    },
    mobile: { intent: 'field' },
    sortKey: 'activityRegistrationsMonth',
    value: (record) => record.activity.registrations.month,
  },
  activityRegistrationsQuarter: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityRegistrationsQuarter',
    label: {
      fallback: 'Regs Quarter',
      key: 'crm.customerDirectory.columns.activityRegistrationsQuarter',
    },
    mobile: { intent: 'field' },
    sortKey: 'activityRegistrationsQuarter',
    value: (record) => record.activity.registrations.quarter,
  },
  activityRegistrationsWeek: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityRegistrationsWeek',
    label: {
      fallback: 'Regs Week',
      key: 'crm.customerDirectory.columns.activityRegistrationsWeek',
    },
    mobile: { intent: 'field' },
    sortKey: 'activityRegistrationsWeek',
    value: (record) => record.activity.registrations.week,
  },
  activityRegistrationsYear: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityRegistrationsYear',
    label: {
      fallback: 'Regs Year',
      key: 'crm.customerDirectory.columns.activityRegistrationsYear',
    },
    mobile: { intent: 'field' },
    sortKey: 'activityRegistrationsYear',
    value: (record) => record.activity.registrations.year,
  },
  activitySchool: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'wide' },
    emptyValue: 'dash',
    formatter: 'activity-school',
    key: 'activitySchool',
    label: { fallback: 'School', key: 'crm.customerDirectory.columns.activitySchool' },
    mobile: { intent: 'field' },
    value: (record) => record.activity.school,
  },
  activityWorkouts: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'activity-counter',
    key: 'activityWorkouts',
    label: { fallback: 'Workouts', key: 'crm.customerDirectory.columns.activityWorkouts' },
    mobile: { intent: 'field' },
    value: (record) => record.activity.workouts,
  },
  birthDate: {
    capability: 'sensitive-fields',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'utc-date',
    key: 'birthDate',
    label: { fallback: 'Birth date', key: 'crm.customerDirectory.columns.birthDate' },
    mobile: { intent: 'field' },
    sortKey: 'birthDate',
    value: (record) => record.birthDate,
  },
  city: {
    capability: 'sensitive-fields',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'city',
    label: { fallback: 'City', key: 'crm.customerDirectory.columns.city' },
    mobile: { intent: 'field' },
    sortKey: 'city',
    value: (record) => record.city,
  },
  country: {
    capability: 'sensitive-fields',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'country',
    label: { fallback: 'Country', key: 'crm.customerDirectory.columns.country' },
    mobile: { intent: 'field' },
    sortKey: 'country',
    value: (record) => record.country,
  },
  currentMembershipExpiration: {
    capability: 'membership',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'utc-date',
    key: 'currentMembershipExpiration',
    label: {
      fallback: 'Expiry date',
      key: 'crm.customerDirectory.columns.membershipExpiration',
    },
    mobile: { intent: 'field' },
    value: (record) => record.currentMembership?.expiresAt,
  },
  currentMembershipNumber: {
    capability: 'membership',
    desktop: { presentation: 'value', size: 'micro' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'currentMembershipNumber',
    label: {
      fallback: 'Membership number',
      key: 'crm.customerDirectory.columns.membershipNumber',
    },
    mobile: { intent: 'field' },
    value: (record) => record.currentMembership?.number,
  },
  currentMembershipStatus: {
    capability: 'membership',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'membership-status',
    key: 'currentMembershipStatus',
    label: {
      fallback: 'Membership status',
      key: 'crm.customerDirectory.columns.membershipStatus',
    },
    mobile: { intent: 'field' },
    value: (record) => record.currentMembership?.status,
  },
  currentMembershipType: {
    capability: 'membership',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'currentMembershipType',
    label: { fallback: 'Membership type', key: 'crm.customerDirectory.columns.membershipType' },
    mobile: { intent: 'field' },
    value: (record) => record.currentMembership?.type.name,
  },
  freediverId: {
    capability: 'document-metadata',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'freediverId',
    label: { fallback: 'Freediver ID', key: 'crm.customerDirectory.columns.freediverId' },
    mobile: { intent: 'field' },
    sortKey: 'freediverId',
    value: (record) => record.freediverId,
  },
  highestDiscount: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'highestDiscount',
    label: { fallback: 'Highest discount', key: 'crm.customerDirectory.columns.highestDiscount' },
    mobile: { intent: 'field' },
    sortKey: 'highestDiscount',
    value: (record) => record.discounts.highest,
  },
  inductingInstructor: {
    capability: 'certification-types',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'inductingInstructor',
    label: {
      fallback: 'Inducting instructor',
      key: 'crm.customerDirectory.columns.inductingInstructor',
    },
    mobile: { intent: 'field' },
    value: (record) => record.inductingInstructor?.displayName,
  },
  instructorId: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'instructorId',
    label: { fallback: 'Instructor ID', key: 'crm.customerDirectory.columns.instructorId' },
    mobile: { intent: 'field' },
    sortKey: 'instructorId',
    value: (record) => record.instructorId,
  },
  lastCertification: {
    capability: 'certification-types',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'lastCertification',
    label: {
      fallback: 'Last certification',
      key: 'crm.customerDirectory.columns.lastCertification',
    },
    mobile: { intent: 'field' },
    value: (record) => record.lastCertification?.type.label,
  },
  lastDocumentStatus: {
    capability: 'document-metadata',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'document-status',
    key: 'lastDocumentStatus',
    label: {
      fallback: 'Document status',
      key: 'crm.customerDirectory.columns.lastDocumentStatus',
    },
    mobile: { intent: 'field' },
    value: (record) => record.lastDocument?.status.label,
  },
  lastDocumentType: {
    capability: 'document-metadata',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'lastDocumentType',
    label: { fallback: 'Document type', key: 'crm.customerDirectory.columns.lastDocumentType' },
    mobile: { intent: 'field' },
    value: (record) => record.lastDocument?.type.label,
  },
  lastDocumentValidUntil: {
    capability: 'document-metadata',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'utc-date',
    key: 'lastDocumentValidUntil',
    label: {
      fallback: 'Valid until',
      key: 'crm.customerDirectory.columns.lastDocumentValidUntil',
    },
    mobile: { intent: 'field' },
    value: (record) => record.lastDocument?.validUntil,
  },
  personalAddress: {
    capability: 'sensitive-fields',
    desktop: { presentation: 'value', size: 'wide' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'personalAddress',
    label: { fallback: 'Personal address', key: 'crm.customerDirectory.columns.personalAddress' },
    mobile: { intent: 'field' },
    value: (record) => record.personalAddress,
  },
  preferredLanguage: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'preferredLanguage',
    label: {
      fallback: 'Preferred language',
      key: 'crm.customerDirectory.columns.preferredLanguage',
    },
    mobile: { intent: 'field' },
    sortKey: 'preferredLanguage',
    value: (record) => record.preferredLanguage,
  },
  registrationsCount: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'registrationsCount',
    label: { fallback: 'Registrations', key: 'crm.customerDirectory.columns.registrationsCount' },
    mobile: { intent: 'field' },
    sortKey: 'registrationCount',
    value: (record) => record.registrationsCount,
  },
  roles: {
    capability: 'none',
    desktop: {
      className: 'px-2 py-3',
      headerClassName: 'px-2',
      presentation: 'roles',
      size: (visibility) => (visibility.view === 'membership' ? 'medium' : 'wide'),
    },
    emptyValue: 'not-provided',
    formatter: 'roles',
    key: 'roles',
    label: { fallback: 'Roles', key: 'crm.customerDirectory.columns.roles' },
    mobile: { intent: 'field' },
    value: (record) => record.roles,
  },
  shopifyId: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'shopifyId',
    label: { fallback: 'Shopify ID', key: 'crm.customerDirectory.columns.shopifyId' },
    mobile: { intent: 'field' },
    sortKey: 'shopifyId',
    value: (record) => record.shopify.id,
  },
  shopifyTags: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'wide' },
    emptyValue: 'dash',
    formatter: 'list',
    key: 'shopifyTags',
    label: { fallback: 'Shopify tags', key: 'crm.customerDirectory.columns.shopifyTags' },
    mobile: { intent: 'field' },
    value: (record) => record.shopify.tags,
  },
  submittingInstructor: {
    capability: 'sensitive-fields',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'submittingInstructor',
    label: {
      fallback: 'Submitting instructor',
      key: 'crm.customerDirectory.columns.submittingInstructor',
    },
    mobile: { intent: 'field' },
    value: (record) => record.submittingInstructor?.displayName,
  },
  user: {
    capability: 'none',
    desktop: {
      className: 'px-2 py-3',
      headerClassName: 'px-2',
      presentation: 'identity',
      size: 'identity',
    },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'user',
    label: { fallback: 'User', key: 'crm.customerDirectory.columns.user' },
    mobile: {
      identity: { value: (record) => record.instructorId, views: ['instructors'] },
      intent: 'header',
    },
    sortKey: 'displayName',
    value: (record) => record.title,
  },
  userOrigin: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'userOrigin',
    label: { fallback: 'User origin', key: 'crm.customerDirectory.columns.userOrigin' },
    mobile: { intent: 'field' },
    sortKey: 'userOrigin',
    value: (record) => record.userOrigin,
  },
  volumeDiscount: {
    capability: 'none',
    desktop: { presentation: 'value', size: 'compact' },
    emptyValue: 'dash',
    formatter: 'text',
    key: 'volumeDiscount',
    label: { fallback: 'Volume discount', key: 'crm.customerDirectory.columns.volumeDiscount' },
    mobile: { intent: 'field' },
    sortKey: 'volumeDiscount',
    value: (record) => record.discounts.volume,
  },
} as const satisfies Record<UserProfilesDirectoryColumnId, UserProfilesDirectoryColumnDefinition>;

/**
 * Rendered Directory columns and their canonical sort affordances. The
 * container reads this same metadata when it renders headers and when it
 * validates URL state for a destination view.
 */
export const USER_PROFILES_DIRECTORY_VIEW_COLUMNS = {
  all: ['user', 'roles', 'actions'],
  freedivers: [
    'user',
    'roles',
    'birthDate',
    'submittingInstructor',
    'preferredLanguage',
    'personalAddress',
    'registrationsCount',
    'actions',
  ],
  instructors: ['user', 'roles', 'lastCertification', 'inductingInstructor', 'actions'],
  activity: [
    'user',
    'roles',
    'activityRegistrationsWeek',
    'activityRegistrationsMonth',
    'activityRegistrationsQuarter',
    'activityRegistrationsHalfYear',
    'activityRegistrationsYear',
    'activityRegistrationsAllTime',
    'activitySchool',
    'activityWorkouts',
    'activityCourses',
    'activityBtPrograms',
    'activityCertificates',
    'actions',
  ],
  membership: [
    'user',
    'roles',
    'currentMembershipNumber',
    'currentMembershipType',
    'currentMembershipStatus',
    'currentMembershipExpiration',
    'actions',
  ],
  shopify: [
    'user',
    'roles',
    'shopifyId',
    'shopifyTags',
    'userOrigin',
    'personalAddress',
    'country',
    'city',
    'actions',
  ],
  discounts: [
    'user',
    'roles',
    'instructorId',
    'lastCertification',
    'currentMembershipType',
    'highestDiscount',
    'volumeDiscount',
    'actions',
  ],
  documents: [
    'user',
    'roles',
    'freediverId',
    'lastDocumentType',
    'lastDocumentStatus',
    'lastDocumentValidUntil',
    'actions',
  ],
} as const satisfies UserProfilesDirectoryViewColumnConfiguration;

export function getUserProfilesDirectoryViewColumns(
  view: UserProfilesDirectoryView | undefined
): readonly UserProfilesDirectoryColumnDefinition[] {
  return USER_PROFILES_DIRECTORY_VIEW_COLUMNS[view ?? 'all'].map(
    (columnId) => USER_PROFILES_DIRECTORY_COLUMN_REGISTRY[columnId]
  );
}

export function isUserProfilesDirectoryColumnVisible(
  column: UserProfilesDirectoryColumnDefinition,
  visibility: UserProfilesDirectoryColumnVisibility
): boolean {
  switch (column.capability) {
    case 'certification-types':
      return visibility.canReadCertificationTypes;
    case 'document-metadata':
      return visibility.canReadDocumentMetadata;
    case 'membership':
      return visibility.canReadMembership;
    case 'sensitive-fields':
      return visibility.canReadSensitiveFields;
    default:
      return true;
  }
}

export function getVisibleUserProfilesDirectoryViewColumns(
  view: UserProfilesDirectoryView | undefined,
  visibility: Omit<UserProfilesDirectoryColumnVisibility, 'view'>
): readonly UserProfilesDirectoryColumnDefinition[] {
  const resolvedVisibility = { ...visibility, view };
  return getUserProfilesDirectoryViewColumns(view).filter((column) =>
    isUserProfilesDirectoryColumnVisible(column, resolvedVisibility)
  );
}

export function getUserProfilesDirectoryColumnSortKey(
  view: UserProfilesDirectoryView | undefined,
  columnKey: string
): UserProfilesDirectorySortKey | undefined {
  return getUserProfilesDirectoryViewColumns(view).find((column) => column.key === columnKey)
    ?.sortKey;
}

export function isUserProfilesDirectorySortSupported(
  view: UserProfilesDirectoryView | undefined,
  sort: UserProfilesDirectorySort | undefined,
  canReadSensitiveFields = false
): boolean {
  if (sort === undefined) return true;
  const [sortKey] = sort.split('.') as [UserProfilesDirectorySortKey, 'asc' | 'desc'];
  return getUserProfilesDirectoryViewColumns(view).some(
    (column) =>
      column.sortKey === sortKey &&
      (column.capability !== 'sensitive-fields' || canReadSensitiveFields)
  );
}

/**
 * A record area selected by a Directory view when opening a customer profile.
 * Other views intentionally use the profile overview.
 */
export type UserProfilesDirectoryProfileArea = 'membership' | 'documents';

/** URL-serializable state owned by the migrated User Profiles directory consumer. */
export interface UserProfilesDirectoryViewState {
  accountLinkage?: 'with' | 'without';
  businessLineKeys?: readonly string[];
  certificationTypeIds?: readonly string[];
  cities?: readonly string[];
  currentMembershipStatuses?: readonly string[];
  freshnessStates?: readonly ('fresh' | 'stale' | 'unknown')[];
  linkedSystems?: readonly ('bubble' | 'move')[];
  membershipTypeIds?: readonly string[];
  page: number;
  profileOrigins?: readonly ('bubble' | 'move' | 'bizagi')[];
  q?: string;
  roles?: readonly string[];
  shopifyCustomerId?: string;
  sort?: UserProfilesDirectorySort;
  view?: UserProfilesDirectoryView;
}

/** Canonical User Profiles entity predicates contributed by a local directory view. */
export interface UserProfilesDirectoryViewQuery {
  hasCurrentInstructorMembership: boolean;
  hasDocuments: boolean;
  hasShopifyCustomerId: boolean;
  roles?: readonly BusinessRole[];
}

/** Mutable router search state that must survive profile navigation and return to the User Profiles directory. */
export interface UserProfilesDirectoryReturnSearch {
  accountLinkage?: 'with' | 'without';
  businessLineKeys?: string[];
  certificationTypeIds?: string[];
  cities?: string[];
  currentMembershipStatuses?: string[];
  freshnessStates?: ('fresh' | 'stale' | 'unknown')[];
  linkedSystems?: ('bubble' | 'move')[];
  membershipTypeIds?: string[];
  page?: number;
  profileOrigins?: ('bubble' | 'move' | 'bizagi')[];
  q?: string;
  roles?: string[];
  shopifyCustomerId?: string;
  sort?: UserProfilesDirectorySort;
  view?: UserProfilesDirectoryView;
}

const DEFAULT_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: false,
  hasShopifyCustomerId: false,
};

const FREEDIVERS_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: false,
  hasShopifyCustomerId: false,
  roles: [BUSINESS_ROLE_FREEDIVER],
};

const INSTRUCTORS_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: false,
  hasShopifyCustomerId: false,
  roles: [BUSINESS_ROLE_INSTRUCTOR],
};

const ACTIVITY_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: false,
  hasShopifyCustomerId: false,
  roles: [BUSINESS_ROLE_INSTRUCTOR],
};

const SHOPIFY_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: false,
  hasShopifyCustomerId: true,
};

const DISCOUNTS_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: false,
  hasShopifyCustomerId: false,
  roles: [BUSINESS_ROLE_INSTRUCTOR],
};

const DOCUMENTS_VIEW_QUERY: UserProfilesDirectoryViewQuery = {
  hasCurrentInstructorMembership: false,
  hasDocuments: true,
  hasShopifyCustomerId: false,
};

/** Maps a frontend-local view to already-published User Profiles query inputs. */
export function getUserProfilesDirectoryViewQuery(
  view: UserProfilesDirectoryView | undefined
): UserProfilesDirectoryViewQuery {
  switch (view) {
    case 'freedivers':
      return FREEDIVERS_VIEW_QUERY;
    case 'instructors':
      return INSTRUCTORS_VIEW_QUERY;
    case 'activity':
      return ACTIVITY_VIEW_QUERY;
    case 'membership':
      return DEFAULT_VIEW_QUERY;
    case 'shopify':
      return SHOPIFY_VIEW_QUERY;
    case 'discounts':
      return DISCOUNTS_VIEW_QUERY;
    case 'documents':
      return DOCUMENTS_VIEW_QUERY;
    default:
      return DEFAULT_VIEW_QUERY;
  }
}

/** Keeps a view-specific record action in the matching customer-record area. */
export function getUserProfilesDirectoryProfileArea(
  view: UserProfilesDirectoryView | undefined
): UserProfilesDirectoryProfileArea | undefined {
  switch (view) {
    case 'documents':
      return 'documents';
    default:
      return undefined;
  }
}

/** Keeps only canonical User Profiles state while navigating to and from a customer record. */
export function getUserProfilesDirectoryReturnSearch({
  accountLinkage,
  page,
  businessLineKeys,
  certificationTypeIds,
  cities,
  currentMembershipStatuses,
  freshnessStates,
  linkedSystems,
  membershipTypeIds,
  profileOrigins,
  q,
  roles,
  shopifyCustomerId,
  sort,
  view,
}: Partial<UserProfilesDirectoryViewState>): UserProfilesDirectoryReturnSearch {
  const result: UserProfilesDirectoryReturnSearch = {};

  if (accountLinkage !== undefined) result.accountLinkage = accountLinkage;
  if (page !== undefined) result.page = page;
  if (businessLineKeys !== undefined) result.businessLineKeys = [...businessLineKeys];
  if (certificationTypeIds !== undefined) result.certificationTypeIds = [...certificationTypeIds];
  if (cities !== undefined) result.cities = [...cities];
  if (currentMembershipStatuses !== undefined) {
    result.currentMembershipStatuses = [...currentMembershipStatuses];
  }
  if (freshnessStates !== undefined) result.freshnessStates = [...freshnessStates];
  if (linkedSystems !== undefined) result.linkedSystems = [...linkedSystems];
  if (membershipTypeIds !== undefined) result.membershipTypeIds = [...membershipTypeIds];
  if (profileOrigins !== undefined) result.profileOrigins = [...profileOrigins];
  if (q !== undefined) result.q = q;
  if (roles !== undefined) result.roles = [...roles];
  if (shopifyCustomerId !== undefined) result.shopifyCustomerId = shopifyCustomerId;
  if (sort !== undefined) result.sort = sort;
  if (view !== undefined && view !== 'membership') result.view = view;

  return result;
}
